import React, { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import Header from "components/Header";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/ui/table";
import {
  useGetStartQuizV2SettingQuery,
  useUpdateStartQuizV2SettingMutation,
  useGetMinAppVersionSettingQuery,
  useUpdateMinAppVersionSettingMutation,
  useGetWithdrawalsSettingQuery,
  useUpdateWithdrawalsSettingMutation,
  useGetNameChangeSettingQuery,
  useUpdateNameChangeSettingMutation,
  useGetLiveDashboardTickerSettingQuery,
  useUpdateLiveDashboardTickerSettingMutation,
} from "../../state/api";

const DEFAULT_LIVE_TICKER =
  "Follow us on YouTube and Instagram · Play free on WishAur · Win Without Losing";

const Settings = () => {
  const {
    data: quizData,
    isLoading: quizLoading,
    isError: quizError,
  } = useGetStartQuizV2SettingQuery();
  const [updateQuizSetting, { isLoading: quizSaving }] =
    useUpdateStartQuizV2SettingMutation();
  const {
    data: minAppData,
    isLoading: minAppLoading,
    isError: minAppError,
    refetch: refetchMinApp,
  } = useGetMinAppVersionSettingQuery();
  const [updateMinAppVersion, { isLoading: minAppSaving }] =
    useUpdateMinAppVersionSettingMutation();

  const {
    data: withdrawData,
    isLoading: withdrawLoading,
    isError: withdrawError,
    refetch: refetchWithdraw,
  } = useGetWithdrawalsSettingQuery();
  const [updateWithdrawals, { isLoading: withdrawSaving }] =
    useUpdateWithdrawalsSettingMutation();

  const {
    data: nameChangeData,
    isLoading: nameChangeLoading,
    isError: nameChangeError,
    refetch: refetchNameChange,
  } = useGetNameChangeSettingQuery();
  const [updateNameChange, { isLoading: nameChangeSaving }] =
    useUpdateNameChangeSettingMutation();

  const {
    data: tickerData,
    isLoading: tickerLoading,
    isError: tickerError,
  } = useGetLiveDashboardTickerSettingQuery();
  const [updateTicker, { isLoading: tickerSaving }] =
    useUpdateLiveDashboardTickerSettingMutation();

  const [pendingKey, setPendingKey] = useState(null);
  const [banner, setBanner] = useState({ type: "", text: "" });
  const [minWithdrawInput, setMinWithdrawInput] = useState("50");
  const [feeThresholdInput, setFeeThresholdInput] = useState("1000");
  const [feeBelowInput, setFeeBelowInput] = useState("10");
  const [feeAboveInput, setFeeAboveInput] = useState("5");
  const [nameFreeCountInput, setNameFreeCountInput] = useState("2");
  const [nameFeeAmountInput, setNameFeeAmountInput] = useState("50");
  const [minAppVersionInput, setMinAppVersionInput] = useState("");
  const [tickerInput, setTickerInput] = useState(DEFAULT_LIVE_TICKER);

  const quizV2Enabled = quizData?.startQuizV2Enabled ?? false;
  const withdrawalsEnabled = withdrawData?.withdrawalsEnabled !== false;
  const minWithdrawAmount = Number(withdrawData?.minWithdrawAmount ?? 50);
  const feeThreshold = Number(withdrawData?.feeThreshold ?? 1000);
  const feeBelow = Number(withdrawData?.feePercentBelow ?? 10);
  const feeAbove = Number(withdrawData?.feePercentAtOrAbove ?? 5);
  const nameFreeCount = Number(nameChangeData?.freeCount ?? 2);
  const nameFeeAmount = Number(nameChangeData?.feeAmount ?? 50);
  const isBootLoading =
    quizLoading || withdrawLoading || nameChangeLoading || minAppLoading;
  const hasLoadError =
    quizError || withdrawError || nameChangeError || minAppError;

  useEffect(() => {
    if (!withdrawData) return;
    if (withdrawData.minWithdrawAmount != null) {
      setMinWithdrawInput(String(withdrawData.minWithdrawAmount));
    }
    if (withdrawData.feeThreshold != null) {
      setFeeThresholdInput(String(withdrawData.feeThreshold));
    }
    if (withdrawData.feePercentBelow != null) {
      setFeeBelowInput(String(withdrawData.feePercentBelow));
    }
    if (withdrawData.feePercentAtOrAbove != null) {
      setFeeAboveInput(String(withdrawData.feePercentAtOrAbove));
    }
  }, [withdrawData]);

  useEffect(() => {
    if (!nameChangeData) return;
    if (nameChangeData.freeCount != null) {
      setNameFreeCountInput(String(nameChangeData.freeCount));
    }
    if (nameChangeData.feeAmount != null) {
      setNameFeeAmountInput(String(nameChangeData.feeAmount));
    }
  }, [nameChangeData]);

  useEffect(() => {
    if (minAppData?.minAppVersion != null) {
      setMinAppVersionInput(String(minAppData.minAppVersion));
    }
  }, [minAppData]);

  useEffect(() => {
    if (tickerData?.liveDashboardTickerMessage != null) {
      setTickerInput(String(tickerData.liveDashboardTickerMessage));
    }
  }, [tickerData]);

  const rows = useMemo(
    () => [
      {
        key: "withdrawals",
        feature: "Withdrawals",
        description:
          "Allow users to submit new withdrawal requests from My Winnings. Existing pending requests are not cancelled.",
        enabled: withdrawalsEnabled,
        statusWhenOn: "Enabled",
        statusWhenOff: "Disabled",
        detailWhenOn: "Users can request withdrawals.",
        detailWhenOff: "New withdraw requests are blocked.",
        actionWhenOn: "Disable",
        actionWhenOff: "Enable",
        destructiveWhenOn: true,
        saving: withdrawSaving && pendingKey === "withdrawals",
        onToggle: async () => {
          setBanner({ type: "", text: "" });
          setPendingKey("withdrawals");
          try {
            await updateWithdrawals({
              withdrawalsEnabled: !withdrawalsEnabled,
            }).unwrap();
            setBanner({
              type: "success",
              text: withdrawalsEnabled
                ? "Withdrawals disabled."
                : "Withdrawals enabled.",
            });
          } catch (error) {
            setBanner({
              type: "error",
              text:
                error?.data?.message || "Failed to update withdrawal setting.",
            });
          } finally {
            setPendingKey(null);
          }
        },
      },
      {
        key: "startQuizV2",
        feature: "Start Quiz API v2",
        description:
          "Require the v2 start-quiz endpoint. Older apps are blocked until they update.",
        enabled: quizV2Enabled,
        statusWhenOn: "Required",
        statusWhenOff: "Optional",
        detailWhenOn: "Only /user/wish/startQuizV2 is allowed.",
        detailWhenOff: "Both startQuiz and startQuizV2 work.",
        actionWhenOn: "Make optional",
        actionWhenOff: "Require v2",
        destructiveWhenOn: false,
        saving: quizSaving && pendingKey === "startQuizV2",
        onToggle: async () => {
          setBanner({ type: "", text: "" });
          setPendingKey("startQuizV2");
          try {
            await updateQuizSetting({
              startQuizV2Enabled: !quizV2Enabled,
            }).unwrap();
            setBanner({
              type: "success",
              text: quizV2Enabled
                ? "Start Quiz v2 requirement disabled."
                : "Start Quiz v2 requirement enabled.",
            });
          } catch (error) {
            setBanner({
              type: "error",
              text: error?.data?.message || "Failed to update setting.",
            });
          } finally {
            setPendingKey(null);
          }
        },
      },
    ],
    [
      withdrawalsEnabled,
      quizV2Enabled,
      withdrawSaving,
      quizSaving,
      pendingKey,
      updateWithdrawals,
      updateQuizSetting,
    ]
  );

  const handleSaveWithdrawRules = async () => {
    setBanner({ type: "", text: "" });
    const minValue = Number(minWithdrawInput);
    const thresholdValue = Number(feeThresholdInput);
    const belowValue = Number(feeBelowInput);
    const aboveValue = Number(feeAboveInput);

    if (!Number.isFinite(minValue) || minValue < 1) {
      setBanner({
        type: "error",
        text: "Minimum withdraw amount must be at least ₹1.",
      });
      return;
    }
    if (!Number.isFinite(thresholdValue) || thresholdValue < 1) {
      setBanner({
        type: "error",
        text: "Fee threshold must be at least ₹1.",
      });
      return;
    }
    if (!Number.isFinite(belowValue) || belowValue < 0 || belowValue > 100) {
      setBanner({
        type: "error",
        text: "Fee % below threshold must be between 0 and 100.",
      });
      return;
    }
    if (!Number.isFinite(aboveValue) || aboveValue < 0 || aboveValue > 100) {
      setBanner({
        type: "error",
        text: "Fee % at/above threshold must be between 0 and 100.",
      });
      return;
    }

    setPendingKey("withdrawRules");
    try {
      await updateWithdrawals({
        minWithdrawAmount: minValue,
        feeThreshold: thresholdValue,
        feePercentBelow: belowValue,
        feePercentAtOrAbove: aboveValue,
      }).unwrap();
      await refetchWithdraw();
      setBanner({
        type: "success",
        text: `Withdrawal rules saved: min ₹${minValue}, below ₹${thresholdValue} → ${belowValue}%, ₹${thresholdValue}+ → ${aboveValue}%.`,
      });
    } catch (error) {
      setBanner({
        type: "error",
        text: error?.data?.message || "Failed to update withdrawal rules.",
      });
    } finally {
      setPendingKey(null);
    }
  };

  const handleSaveNameChangeRules = async () => {
    setBanner({ type: "", text: "" });
    const freeCount = Number(nameFreeCountInput);
    const feeAmount = Number(nameFeeAmountInput);
    if (!Number.isInteger(freeCount) || freeCount < 0) {
      setBanner({
        type: "error",
        text: "Free name changes must be a whole number >= 0.",
      });
      return;
    }
    if (!Number.isFinite(feeAmount) || feeAmount < 0) {
      setBanner({
        type: "error",
        text: "Name change fee must be a number >= 0.",
      });
      return;
    }
    setPendingKey("nameChangeRules");
    try {
      await updateNameChange({ freeCount, feeAmount }).unwrap();
      await refetchNameChange();
      setBanner({
        type: "success",
        text: `Name change rules saved: first ${freeCount} free, then ₹${feeAmount} each.`,
      });
    } catch (error) {
      setBanner({
        type: "error",
        text: error?.data?.message || "Failed to update name change rules.",
      });
    } finally {
      setPendingKey(null);
    }
  };

  const handleSaveTicker = async () => {
    const value = tickerInput.trim();
    if (!value) {
      setBanner({
        type: "error",
        text: "Live dashboard ticker message cannot be empty.",
      });
      return;
    }
    if (value.length > 500) {
      setBanner({
        type: "error",
        text: "Ticker message must be at most 500 characters.",
      });
      return;
    }
    setPendingKey("liveTicker");
    setBanner({ type: "", text: "" });
    try {
      await updateTicker({ liveDashboardTickerMessage: value }).unwrap();
      setBanner({
        type: "success",
        text: "Live dashboard ticker message saved.",
      });
    } catch (error) {
      setBanner({
        type: "error",
        text:
          error?.data?.message || "Failed to update live dashboard ticker.",
      });
    } finally {
      setPendingKey(null);
    }
  };

  const handleSaveMinAppVersion = async () => {
    const value = minAppVersionInput.trim();
    if (value && !/^\d+\.\d+(\.\d+)?$/.test(value)) {
      setBanner({
        type: "error",
        text: "Use a version like 1.1.48, or leave empty to disable.",
      });
      return;
    }
    setBanner({ type: "", text: "" });
    setPendingKey("minAppVersion");
    try {
      await updateMinAppVersion({ minAppVersion: value }).unwrap();
      await refetchMinApp();
      setBanner({
        type: "success",
        text: value
          ? `Users below ${value} must update before starting a quiz.`
          : "Minimum app version cleared. All versions can play.",
      });
    } catch (error) {
      setBanner({
        type: "error",
        text: error?.data?.message || "Failed to update minimum app version.",
      });
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <Header
        title="Settings"
        subtitle="Manage app feature flags and API controls"
      />

      {banner.text ? (
        <div
          className={`rounded-md border px-4 py-2 text-sm ${
            banner.type === "success"
              ? "border-green-200 bg-green-50 text-green-700"
              : "border-red-200 bg-red-50 text-red-700"
          }`}
        >
          {banner.text}
        </div>
      ) : null}

      <div className="rounded-lg border bg-card">
        <div className="flex flex-col gap-1 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold">Feature controls</h2>
            <p className="text-sm text-muted-foreground">
              Toggle app capabilities from one place. New settings can be added
              as rows here.
            </p>
          </div>
          {(quizSaving ||
            withdrawSaving ||
            nameChangeSaving ||
            minAppSaving) && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving…
            </div>
          )}
        </div>

        {isBootLoading ? (
          <div className="flex items-center gap-2 px-4 py-10 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading settings…
          </div>
        ) : hasLoadError ? (
          <p className="px-4 py-10 text-sm text-red-600">
            Failed to load one or more settings. Refresh and try again.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[160px]">Feature</TableHead>
                  <TableHead className="min-w-[280px]">Description</TableHead>
                  <TableHead className="w-[120px]">Status</TableHead>
                  <TableHead className="min-w-[220px]">Details</TableHead>
                  <TableHead className="w-[140px] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.key}>
                    <TableCell className="align-top font-medium">
                      {row.feature}
                    </TableCell>
                    <TableCell className="align-top text-sm text-muted-foreground">
                      {row.description}
                    </TableCell>
                    <TableCell className="align-top">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${
                          row.enabled
                            ? "bg-green-100 text-green-800"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {row.enabled ? row.statusWhenOn : row.statusWhenOff}
                      </span>
                    </TableCell>
                    <TableCell className="align-top text-sm text-muted-foreground">
                      {row.enabled ? row.detailWhenOn : row.detailWhenOff}
                    </TableCell>
                    <TableCell className="align-top text-right">
                      <Button
                        size="sm"
                        variant={
                          row.enabled && row.destructiveWhenOn
                            ? "destructive"
                            : "outline"
                        }
                        disabled={!!pendingKey}
                        onClick={row.onToggle}
                      >
                        {row.saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : row.enabled ? (
                          row.actionWhenOn
                        ) : (
                          row.actionWhenOff
                        )}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold">Live dashboard ticker</h2>
            <p className="text-sm text-muted-foreground">
              Running message shown at the bottom of the Live Ranking TV board.
              Keep it short; it scrolls continuously.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleSaveTicker}
            disabled={!!pendingKey || tickerSaving || tickerLoading}
          >
            {pendingKey === "liveTicker" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Save ticker"
            )}
          </Button>
        </div>
        <div className="px-4 py-4 space-y-3">
          {tickerLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading ticker…
            </div>
          ) : tickerError ? (
            <p className="text-sm text-red-600">Failed to load ticker message.</p>
          ) : (
            <>
              <label className="text-sm font-medium">
                Message ({tickerInput.trim().length}/500)
              </label>
              <textarea
                className="mt-2 flex min-h-[88px] w-full max-w-3xl rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                value={tickerInput}
                maxLength={500}
                onChange={(e) => setTickerInput(e.target.value)}
                placeholder={DEFAULT_LIVE_TICKER}
              />
              <div className="max-w-3xl overflow-hidden rounded-full bg-[#111827] px-4 py-2">
                <p className="truncate text-xs font-semibold text-white">
                  Preview: {tickerInput.trim() || DEFAULT_LIVE_TICKER}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold">Force Android update</h2>
            <p className="text-sm text-muted-foreground">
              Applies to Android only. iOS is never blocked. After the new
              Play Store build is live, set the minimum version (e.g. 1.1.48).
              Older Android apps get “Please update the app” on Quiz Now.
              Leave empty to allow every Android version.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleSaveMinAppVersion}
            disabled={!!pendingKey || minAppSaving || isBootLoading}
          >
            {pendingKey === "minAppVersion" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Save minimum version"
            )}
          </Button>
        </div>
        <div className="px-4 py-4">
          <label className="text-sm font-medium">
            Minimum Android version
          </label>
          <Input
            className="mt-2 max-w-xs"
            placeholder="1.1.48"
            value={minAppVersionInput}
            onChange={(e) => setMinAppVersionInput(e.target.value)}
          />
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold">Withdrawal rules</h2>
            <p className="text-sm text-muted-foreground">
              Active now: below ₹{feeThreshold} → {feeBelow}%, ₹{feeThreshold}+ →{" "}
              {feeAbove}%. Min request ₹{minWithdrawAmount}.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleSaveWithdrawRules}
            disabled={!!pendingKey || withdrawSaving || isBootLoading}
          >
            {pendingKey === "withdrawRules" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Save rules"
            )}
          </Button>
        </div>

        {isBootLoading ? (
          <div className="flex items-center gap-2 px-4 py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading withdrawal rules…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Rule</TableHead>
                  <TableHead className="min-w-[280px]">Description</TableHead>
                  <TableHead className="min-w-[200px]">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="align-top font-medium">
                    Minimum withdraw
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    Lowest amount a user can request.
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        min={1}
                        step="1"
                        value={minWithdrawInput}
                        onChange={(e) => setMinWithdrawInput(e.target.value)}
                        className="max-w-[140px]"
                        disabled={pendingKey === "withdrawRules"}
                      />
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="align-top font-medium">
                    Fee threshold
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    Amounts below this use the higher fee %; at/above use the
                    lower fee %.
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        min={1}
                        step="1"
                        value={feeThresholdInput}
                        onChange={(e) => setFeeThresholdInput(e.target.value)}
                        className="max-w-[140px]"
                        disabled={pendingKey === "withdrawRules"}
                      />
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="align-top font-medium">
                    Fee below threshold
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    Applied when requested amount is less than the threshold.
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="0.1"
                        value={feeBelowInput}
                        onChange={(e) => setFeeBelowInput(e.target.value)}
                        className="max-w-[140px]"
                        disabled={pendingKey === "withdrawRules"}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="align-top font-medium">
                    Fee at/above threshold
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    Applied when requested amount is threshold or more.
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="0.1"
                        value={feeAboveInput}
                        onChange={(e) => setFeeAboveInput(e.target.value)}
                        className="max-w-[140px]"
                        disabled={pendingKey === "withdrawRules"}
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <div className="rounded-lg border bg-card">
        <div className="flex flex-col gap-3 border-b px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-base font-semibold">Name change rules</h2>
            <p className="text-sm text-muted-foreground">
              Active now: first {nameFreeCount} name-field changes free, then ₹
              {nameFeeAmount} each. First &amp; last name each count as one
              change.
            </p>
          </div>
          <Button
            size="sm"
            onClick={handleSaveNameChangeRules}
            disabled={!!pendingKey || nameChangeSaving || isBootLoading}
          >
            {pendingKey === "nameChangeRules" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Save rules"
            )}
          </Button>
        </div>

        {isBootLoading ? (
          <div className="flex items-center gap-2 px-4 py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading name change rules…
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Rule</TableHead>
                  <TableHead className="min-w-[280px]">Description</TableHead>
                  <TableHead className="min-w-[200px]">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="align-top font-medium">
                    Free changes
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    How many name-field edits are free before charging.
                  </TableCell>
                  <TableCell className="align-top">
                    <Input
                      type="number"
                      min={0}
                      step="1"
                      value={nameFreeCountInput}
                      onChange={(e) => setNameFreeCountInput(e.target.value)}
                      className="max-w-[140px]"
                      disabled={pendingKey === "nameChangeRules"}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="align-top font-medium">
                    Paid change fee
                  </TableCell>
                  <TableCell className="align-top text-sm text-muted-foreground">
                    Flat wallet deduction for each name-field change after the
                    free quota.
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">₹</span>
                      <Input
                        type="number"
                        min={0}
                        step="1"
                        value={nameFeeAmountInput}
                        onChange={(e) => setNameFeeAmountInput(e.target.value)}
                        className="max-w-[140px]"
                        disabled={pendingKey === "nameChangeRules"}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Settings;
